import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';

export type PositionActivationItem = {
  id: string;
  incident_id: string;
  position_id: string;
  label: string;
  done: boolean;
  sort_order: number;
};

const keys = {
  list: (positionId: string) =>
    ['position-activation-items', positionId] as const,
};

export function usePositionActivationItems(positionId: string | undefined) {
  return useQuery({
    queryKey: keys.list(positionId ?? ''),
    enabled: Boolean(positionId),
    queryFn: async () => {
      const response = await axios.get<PositionActivationItem[]>(
        config.apiGateway.positionActivationItems(positionId!)
      );
      return response.data;
    },
  });
}

export function useCreatePositionActivationItem(positionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const response = await axios.post<PositionActivationItem>(
        config.apiGateway.positionActivationItems(positionId),
        { label }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.list(positionId) });
    },
    onError: () => {
      toast.error('Could not add the checklist item');
    },
  });
}

export function useUpdatePositionActivationItem(positionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      label?: string;
      done?: boolean;
    }) => {
      const response = await axios.patch<PositionActivationItem>(
        config.apiGateway.positionActivationItem(positionId, input.itemId),
        { label: input.label, done: input.done }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.list(positionId) });
    },
    onError: () => {
      toast.error('Could not update the checklist item');
    },
  });
}

export function useDeletePositionActivationItem(positionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      await axios.delete(
        config.apiGateway.positionActivationItem(positionId, itemId)
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.list(positionId) });
    },
    onError: () => {
      toast.error('Could not remove the checklist item');
    },
  });
}
